import { useState, useEffect, useCallback } from 'react'
import GenericTable from '../GenericTable'
import { d1Query, d1Insert, d1Update, d1Delete } from '../../services/d1Service'
import type { StudentStat, StatusMessage } from '../../types'

interface Props { eiin: string; onStatus: (msg: StatusMessage) => void }

export default function StudentStatsTab({ eiin, onStatus }: Props) {
  const [items, setItems] = useState<StudentStat[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<Partial<StudentStat> | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try { setItems(await d1Query<StudentStat>(`SELECT * FROM student_stats WHERE institution_eiin = ? ORDER BY id ASC`, [eiin])) }
    catch (e: any) { onStatus({ type: 'error', text: e.message }) }
    finally { setLoading(false) }
  }, [eiin])

  useEffect(() => { load() }, [load])

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editing) return
    try {
      const rec = { ...editing, institution_eiin: eiin }
      if ((editing as any).id) await d1Update('student_stats', (editing as any).id, rec)
      else await d1Insert('student_stats', rec)
      onStatus({ type: 'success', text: 'Student stat saved!' })
      setEditing(null); load()
    } catch (e: any) { onStatus({ type: 'error', text: e.message }) }
  }

  return (
    <GenericTable
      title="Student Stats" icon="📊"
      items={items} loading={loading} editingItem={editing}
      columns={[
        { key: 'class_name', label: 'Class', width: '130px' },
        { key: 'gender', label: 'Gender', width: '80px' },
        { key: 'student_count', label: 'Count', width: '80px' },
        { key: 'academic_year', label: 'Year', width: '80px' }
      ]}
      formFields={[
        { key: 'class_name', label: 'Class Name', required: true },
        { key: 'gender', label: 'Gender', type: 'select', options: [
          { value: 'male', label: 'Male' },
          { value: 'female', label: 'Female' },
          { value: 'both', label: 'Both' }
        ]},
        { key: 'student_count', label: 'Student Count', type: 'number', required: true },
        { key: 'academic_year', label: 'Academic Year', required: true }
      ]}
      onAdd={() => setEditing({ class_name: '', gender: 'both', student_count: 0, academic_year: '2026' })}
      onEdit={(item) => setEditing({ ...item })}
      onDelete={async (id) => { try { await d1Delete('student_stats', id); onStatus({ type: 'success', text: 'Deleted!' }); load() } catch (e: any) { onStatus({ type: 'error', text: e.message }) } }}
      onSave={handleSave}
      onCancel={() => setEditing(null)}
      onFieldChange={(key, val) => setEditing(prev => prev ? { ...prev, [key]: val } : prev)}
    />
  )
}
